import { useRecipeHistoryService } from "../../Hooks/useRecipeHistoryService";
import { useRecipeService } from "../../Hooks/useRecipeService";
import { PageHeader } from "../../Shared/PageHeader";
import PageOverlay from "../../Shared/PageOverlay";
import React, { useState, useEffect } from "react";
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Button,
	Typography,
	Checkbox,
	IconButton,
	Tooltip
} from "@material-ui/core";
import { Link } from "react-router-dom";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CompareArrowsIcon from "@material-ui/icons/CompareArrows";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

export function RecipeHistory(props) {
	const recipeHistoryService = useRecipeHistoryService(props.config);
	const recipeService = useRecipeService(props.config);
	const [history, setHistory] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [recipeName, setRecipeName] = useState("");
	const [selectedVersions, setSelectedVersions] = useState([]);
	const recipeId = props.match.params.recipeId;

	useEffect(() => {
		document.title = `Recipe History - ${props.config.appName}`;
		setIsLoading(true);

		// Get the current recipe name
		recipeService.getRecipeById(recipeId, "", (response) => {
			if (response && response.data) {
				setRecipeName(response.data.name);
				document.title = `${response.data.name} - History - ${props.config.appName}`;
			}
		}, (error) => {
			console.error(error);
		});

		// Get history with current recipe
		recipeHistoryService.getRecipeHistory(recipeId, true, (response) => {
			if (response && response.data) {
				setHistory(response.data);
			}
			setIsLoading(false);
		}, (error) => {
			console.error(error);
			if (error.response && error.response.status === 404) {
				props.history.push("/notfound");
			}
			setIsLoading(false);
		});
	}, [recipeId]); // eslint-disable-line react-hooks/exhaustive-deps

	const formatDateTime = (dateTimeString) => {
		if (!dateTimeString) return "";
		const date = new Date(dateTimeString);
		return date.toLocaleString();
	};

	const getEntryDate = (entry) => {
		const dateValue = entry?.recipeHistoryDateTime || entry?.updateDateTime;
		return dateValue ? new Date(dateValue) : new Date(0);
	};

	const handleCheckboxChange = (entry) => {
		const id = entry.recipeHistoryId;
		setSelectedVersions((previous) => {
			const isCurrentlySelected = previous.some(v => v.recipeHistoryId === id);
			if (isCurrentlySelected) {
				return previous.filter(v => v.recipeHistoryId !== id);
			}
			if (previous.length < 2) {
				return [...previous, entry];
			}
			return previous;
		});
	};

	const handleCompare = () => {
		if (selectedVersions.length !== 2) {
			return;
		}

		const sorted = [...selectedVersions].sort((a, b) => getEntryDate(a) - getEntryDate(b));
		const toRouteId = (entry) => entry.recipeHistoryId === 0 ? "current" : entry.recipeHistoryId;
		const id1 = toRouteId(sorted[0]);
		const id2 = toRouteId(sorted[1]);

		props.history.push(`/recipes/${recipeId}/history/compare/${id1}/${id2}`);
	};

	const isVersionSelected = (id) => {
		return selectedVersions.some(v => v.recipeHistoryId === id);
	};

	const canCompare = selectedVersions.length === 2;

	return (
		<React.Fragment>
			<PageHeader text={`${recipeName} - History`} config={props.config} />
			<PageOverlay showOverlay={isLoading} />
			<div style={{ marginBottom: 12 }}>
				<Button
					component={Link}
					to={`/recipes/${recipeId}`}
					variant="contained"
					color="primary">
					<ArrowBackIcon style={{ marginRight: 8 }} />
					Back to Recipe
				</Button>
			</div>
			<Paper style={{ padding: 12 }}>
				<div style={{ marginBottom: 16 }}>
					<Tooltip title={canCompare ? "Compare selected versions" : "Select 2 recipes to compare"}>
						<span>
							<Button
								variant="contained"
								color="primary"
								onClick={handleCompare}
								disabled={!canCompare}
								startIcon={<CompareArrowsIcon />}>
								Compare Selected Versions
							</Button>
						</span>
					</Tooltip>
				</div>
				{history.length === 0 && !isLoading ? (
					<Typography>No history available for this recipe.</Typography>
				) : (
					<Table className="nowrap-table condensed-table">
						<TableHead>
							<TableRow>
								<TableCell align="center" style={{ width: 1, padding: 4 }}></TableCell>
								<TableCell>Version</TableCell>
								<TableCell>Date Changed</TableCell>
								<TableCell>Recipe Name</TableCell>
								<TableCell>Changed By</TableCell>
								<TableCell>Change Reason</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{history.map((entry, index) => {
								const isCurrent = entry.recipeHistoryId === 0;
								const viewUrl = isCurrent
									? `/recipes/${recipeId}`
									: `/recipes/${recipeId}/history/${entry.recipeHistoryId}`;
								const dateLabel = `${formatDateTime(entry.recipeHistoryDateTime)}${isCurrent ? " (Current)" : ""}`;
								const changedBy = entry.username || entry.updateUsername || "Unknown";

								return (
									<TableRow
										key={entry.recipeHistoryId || 'current'}
										style={isCurrent ? { backgroundColor: '#f5f5f5', fontWeight: 'bold' } : {}}>
										<TableCell align="center" style={{ width: 1, padding: 4 }}>
											<div style={{ display: "flex", alignItems: "center", gap: 2 }}>
												<Checkbox
													size="small"
													style={{ padding: 2 }}
													checked={isVersionSelected(entry.recipeHistoryId)}
													onChange={() => handleCheckboxChange(entry)}
													disabled={selectedVersions.length >= 2 && !isVersionSelected(entry.recipeHistoryId)}
													color="primary" />
												<Tooltip title={isCurrent ? "View current recipe" : "View recipe"}>
													<IconButton
														component={Link}
														to={viewUrl}
														color="primary"
														size="small"
														style={{ padding: 4 }}
														className="rb-icon-btn"
														aria-label={isCurrent ? "View current recipe" : "View recipe"}>
														<VisibilityIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</div>
										</TableCell>
										<TableCell>{isCurrent ? "Current" : entry.recipeHistoryId}</TableCell>
										<TableCell>{dateLabel}</TableCell>
										<TableCell>{entry.name}</TableCell>
										<TableCell>{changedBy}</TableCell>
										<TableCell>{entry.changeReason || 'N/A'}</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				)}
			</Paper>
		</React.Fragment>
	);
}
